import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  getDoc, // <-- added
} from 'firebase/firestore';
import { db } from '@root/firebase';
import { toast } from 'react-hot-toast';

/* ---------- helpers ---------- */
function formatShortDate(ts) {
  const d = ts?.toDate ? ts.toDate() : ts;
  if (!d) return '';
  const dd = d.getDate().toString().padStart(2, '0');
  const mon = d.toLocaleString('en-US', { month: 'short' });
  const yy = d.getFullYear().toString().slice(-2);
  const hr = d.getHours().toString().padStart(2, '0');
  const mi = d.getMinutes().toString().padStart(2, '0');
  return `${dd} ${mon} ${yy} ${hr}:${mi}`;
}

function preview(body = '', n = 80) {
  const text = body.replace(/\s+/g, ' ').trim();
  return text.length <= n ? text : text.slice(0, n) + '…';
}

/**
 * Firestore per-user mailbox:
 * users/{uid}/mail/{messageId}
 * {
 *   threadId, folder: 'inbox'|'sent'|'archive'|'trash',
 *   toUserId,toName, fromUserId,fromName,fromEmail,
 *   isAdminRecipient, feature, issue,
 *   subject, body,
 *   read, readAt, important,
 *   createdAt
 * }
 */
const FEATURE_OPTIONS = [
  'DASHBOARD',
  'SMART_PROFILE',
  'SMART_PET',
  'SMART_LIBRARY',
  'SMART_DIRECTORY',
  'SMART_MEMORIAL',
  'SMARTIFY',
  'SMART_QUESTION',
  'SMART_CALENDAR',
  'OTHER',
];

const ISSUE_OPTIONS = [
  'GENERAL',
  'SUBSCRIPTION',
  'ACCOUNTS',
  'PLATFORM_ISSUE',
  'FEEDBACK',
  'IMPROVEMENT',
  'OTHER',
];

export default function InternalEmail() {
  const { globalUser, globalData } = useAuth();
  const uid = globalUser?.uid;

  // UI state
  const [activeFolder, setActiveFolder] = useState('inbox'); // 'inbox' | 'sent' | 'archive' | 'trash'
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Compose state
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [recipientId, setRecipientId] = useState('');
  const [recipientType, setRecipientType] = useState('connection'); // 'connection' | 'admin'
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [feature, setFeature] = useState('');
  const [issue, setIssue] = useState('');

  // Recipient lists
  const [connections, setConnections] = useState([]); // [{id,name}]
  const [admins, setAdmins] = useState([]); // [{id,name,email}]

  /* ------------ Load connections list ------------ */
  useEffect(() => {
    if (!uid) return;
    const connRef = collection(db, `users/${uid}/connections`);
    const unsub = onSnapshot(connRef, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setConnections(rows);
    });
    return () => unsub();
  }, [uid]);

  /* ------------ Load admins ------------ */
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, 'users'), where('isAdmin', '==', true));
        const qs = await getDocs(q);
        const rows = qs.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setAdmins(rows);
      } catch (e) {
        console.error('Failed to load admins', e);
      }
    })();
  }, []);

  /* ------------ Live mailbox by folder ------------ */
  useEffect(() => {
    if (!uid) return;
    const mailRef = collection(db, `users/${uid}/mail`);
    const qRef = query(
      mailRef,
      where('folder', '==', activeFolder),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(rows);
        setSelectedIds(new Set()); // clear selection when folder changes
        setSelectedMessage(null);
      },
      (err) => console.error('mailbox error', err)
    );
    return () => unsub();
  }, [uid, activeFolder]);

  /* ------------ Name cache to show names, not UIDs ------------ */
  const [nameCache, setNameCache] = useState({}); // { [uid]: "Display Name" }

  async function resolveUserName(userId) {
    if (!userId) return '';
    if (nameCache[userId]) return nameCache[userId];
    try {
      const s = await getDoc(doc(db, 'users', userId));
      const name = s.exists()
        ? s.data().name || s.data().displayName || s.data().email || userId
        : userId;
      setNameCache((prev) => ({ ...prev, [userId]: name }));
      return name;
    } catch {
      return userId;
    }
  }

  useEffect(() => {
    messages.forEach((m) => {
      if (!m?.fromName) resolveUserName(m?.fromUserId);
      if (!m?.toName) resolveUserName(m?.toUserId);
    });
  }, [messages]);

  /* ------------ Open -> mark read ------------ */
  const handleOpenMessage = async (msg) => {
    setSelectedMessage(msg);
    if (activeFolder === 'inbox' && !msg.read && uid) {
      try {
        await updateDoc(doc(db, `users/${uid}/mail/${msg.id}`), {
          read: true,
          readAt: serverTimestamp(),
        });
      } catch (e) {
        console.error('mark read error', e);
      }
    }
  };

  /* ------------ Row selection handlers ------------ */
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllOnPage = () => {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map((m) => m.id)));
    }
  };

  /* ------------ Per-row star toggle ------------ */
  const toggleImportant = async (msg) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, `users/${uid}/mail/${msg.id}`), {
        important: !msg.important,
      });
    } catch (e) {
      console.error('toggle important error', e);
    }
  };

  /* ------------ Bulk toolbar actions ------------ */
  const runBulk = async (action) => {
    if (!uid) return;
    if (selectedIds.size === 0) return; // no-op if nothing selected

    try {
      const batch = writeBatch(db);
      const selectedArr = messages.filter((m) => selectedIds.has(m.id));

      if (action === 'archive') {
        selectedArr.forEach((m) =>
          batch.update(doc(db, `users/${uid}/mail/${m.id}`), {
            folder: 'archive',
          })
        );
      } else if (action === 'trash') {
        selectedArr.forEach((m) =>
          batch.update(doc(db, `users/${uid}/mail/${m.id}`), {
            folder: 'trash',
          })
        );
      } else if (action === 'important') {
        // Mark as important (not toggle) for all selected.
        selectedArr.forEach((m) =>
          batch.update(doc(db, `users/${uid}/mail/${m.id}`), {
            important: true,
          })
        );
      }

      await batch.commit();
      toast.success(
        action === 'archive'
          ? 'Archived'
          : action === 'trash'
          ? 'Moved to Trash'
          : 'Marked as important'
      );
      setSelectedIds(new Set());
    } catch (e) {
      console.error('bulk action error', e);
      toast.error('Action failed');
    }
  };

  /* ------------ Compose send ------------ */
  const canSend = useMemo(() => {
    if (!recipientId || !subject.trim() || !body.trim()) return false;
    if (recipientType === 'admin' && (!feature || !issue)) return false;
    return true;
  }, [recipientId, subject, body, recipientType, feature, issue]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!uid) return toast.error('You must be logged in.');
    if (!canSend) return toast.error('Please complete all required fields.');

    try {
      const batch = writeBatch(db);
      const rec =
        recipientType === 'admin'
          ? admins.find((a) => a.id === recipientId)
          : connections.find((c) => c.id === recipientId);
      if (!rec) return toast.error('Recipient not found.');

      const toUserId = rec.id;
      const toName = rec.name || rec.displayName || rec.email || 'User';
      const fromName = globalData?.name || globalUser?.email || 'User';
      const fromEmail = globalUser?.email || '';

      const inboxRef = doc(collection(db, `users/${toUserId}/mail`));
      const sentRef = doc(collection(db, `users/${uid}/mail`));
      const sharedId = inboxRef.id;

      const base = {
        threadId: sharedId,
        toUserId,
        toName,
        fromUserId: uid,
        fromName,
        fromEmail,
        isAdminRecipient: recipientType === 'admin',
        feature: recipientType === 'admin' ? feature : null,
        issue: recipientType === 'admin' ? issue : null,
        subject: subject.trim(),
        body: body.trim(),
        read: false,
        readAt: null,
        important: false, // <-- default
        createdAt: serverTimestamp(),
      };

      batch.set(inboxRef, { ...base, folder: 'inbox' });
      batch.set(sentRef, { ...base, folder: 'sent', read: true });

      await batch.commit();

      setRecipientId('');
      setSubject('');
      setBody('');
      setFeature('');
      setIssue('');
      setRecipientType('connection');
      setIsComposeOpen(false);
      setActiveFolder('sent');
      toast.success('Message sent');
    } catch (e) {
      console.error('send error', e);
      toast.error('Failed to send message');
    }
  };

  /* ------------ Recipient options ------------ */
  const connectionOptions = useMemo(
    () =>
      connections.map((c) => ({
        id: c.id,
        label: c.name || c.displayName || c.username || c.id,
      })),
    [connections]
  );
  const adminOptions = useMemo(
    () =>
      admins.map((a) => ({
        id: a.id,
        label: a.name || a.displayName || a.email || a.id,
      })),
    [admins]
  );

  return (
    <Layout>
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Assist</li>
            <li className='breadcrumb-item'>Messages</li>
          </ol>
        </div>
      </div>

      <div className='row'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-body d-flex '>
              {/* Left nav */}
              <div className='email-left-box border-end' style={{ width: 240 }}>
                <div className='mail-list rounded mt-2'>
                  <button
                    className={`list-group-item w-100 text-start ${
                      activeFolder === 'inbox' ? 'active' : ''
                    }`}
                    onClick={() => setActiveFolder('inbox')}
                  >
                    <i className='fa fa-inbox font-18 align-middle me-2' />{' '}
                    Inbox
                  </button>
                  <button
                    className={`list-group-item w-100 text-start ${
                      activeFolder === 'sent' ? 'active' : ''
                    }`}
                    onClick={() => setActiveFolder('sent')}
                  >
                    <i className='fa fa-paper-plane font-18 align-middle me-2' />{' '}
                    Sent
                  </button>
                  <button
                    className={`list-group-item w-100 text-start ${
                      activeFolder === 'archive' ? 'active' : ''
                    }`}
                    onClick={() => setActiveFolder('archive')}
                  >
                    <i className='fa fa-archive font-18 align-middle me-2' />{' '}
                    Archive
                  </button>
                  <button
                    className={`list-group-item w-100 text-start ${
                      activeFolder === 'trash' ? 'active' : ''
                    }`}
                    onClick={() => setActiveFolder('trash')}
                  >
                    <i className='fa fa-trash font-18 align-middle me-2' />{' '}
                    Trash
                  </button>
                </div>
              </div>

              {/* Right: list + read pane */}
              <div className='email-right-box flex-grow-1 px-4 py-3'>
                {/* Toolbar */}
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <div className='d-flex align-items-center gap-2'>
                    <button
                      className='btn btn-primary btn-sm'
                      onClick={() => setIsComposeOpen(true)}
                    >
                      Compose
                    </button>
                    <div className='form-check'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        id='chk-all'
                        onChange={selectAllOnPage}
                        checked={
                          messages.length > 0 &&
                          selectedIds.size === messages.length
                        }
                      />
                      <label className='form-check-label' htmlFor='chk-all'>
                        Select all
                      </label>
                    </div>
                  </div>

                  <div className='btn-group'>
                    <button
                      type='button'
                      className='btn btn-primary light px-3'
                      onClick={() => runBulk('archive')}
                      disabled={selectedIds.size === 0}
                      title='Archive'
                    >
                      <i className='fa fa-archive'></i>
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary light px-3'
                      onClick={() => runBulk('important')}
                      disabled={selectedIds.size === 0}
                      title='Mark important'
                    >
                      <i className='fa fa-star'></i>
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary light px-3'
                      onClick={() => runBulk('trash')}
                      disabled={selectedIds.size === 0}
                      title='Move to trash'
                    >
                      <i className='fa fa-trash'></i>
                    </button>
                  </div>
                </div>

                {/* LIST */}
                <div className='email-list'>
                  {messages.length === 0 ? (
                    <div className='text-muted p-4'>No messages.</div>
                  ) : (
                    messages.map((m) => {
                      const checkboxId = `chk-${m.id}`;
                      const isUnread = activeFolder === 'inbox' && !m.read;
                      const senderLabel =
                        activeFolder === 'inbox'
                          ? m.fromName ||
                            nameCache[m.fromUserId] ||
                            m.fromEmail ||
                            m.fromUserId
                          : m.toName || nameCache[m.toUserId] || m.toUserId;

                      return (
                        <div key={m.id} className='message py-2 border-bottom'>
                          <div className='d-flex align-items-center'>
                            {/* Select */}
                            <div className='ps-1'>
                              <div className='form-check custom-checkbox'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={checkboxId}
                                  checked={selectedIds.has(m.id)}
                                  onChange={() => toggleSelected(m.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label
                                  className='form-check-label'
                                  htmlFor={checkboxId}
                                ></label>
                              </div>
                            </div>

                            {/* Star */}
                            <button
                              className='border-0 bg-transparent align-middle p-0 ms-2'
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleImportant(m);
                              }}
                              title={m.important ? 'Unstar' : 'Star'}
                            >
                              <i
                                className={`fa ${
                                  m.important ? 'fa-star' : 'fa-star-o'
                                }`}
                              />
                            </button>

                            {/* Clickable content */}
                            <div
                              className='col-mail col-mail-2 ms-3 flex-grow-1'
                              role='button'
                              onClick={() => handleOpenMessage(m)}
                            >
                              <div
                                className={`d-flex justify-content-between align-items-center`}
                              >
                                <div
                                  className='subject text-truncate'
                                  style={{ maxWidth: '70%' }}
                                >
                                  <span className={isUnread ? 'fw-bold' : ''}>
                                    {senderLabel}
                                  </span>
                                  <span className='mx-1'>–</span>
                                  <span className={isUnread ? 'fw-bold' : ''}>
                                    {m.subject || '(no subject)'}
                                  </span>
                                  <span className='text-muted'>
                                    {' '}
                                    · {preview(m.body)}
                                  </span>
                                </div>
                                <div className='date text-nowrap text-muted'>
                                  {formatShortDate(m.createdAt)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* READ PANE */}
                {selectedMessage && (
                  <div className='card mt-3'>
                    <div className='card-body'>
                      <div className='d-flex justify-content-between align-items-center'>
                        <h5 className='mb-0'>
                          {selectedMessage.subject || '(no subject)'}
                        </h5>
                        <div className='text-muted'>
                          {formatShortDate(selectedMessage.createdAt)}
                        </div>
                      </div>
                      <div className='mt-1 text-muted'>
                        From:{' '}
                        {selectedMessage.fromName || selectedMessage.fromEmail}{' '}
                        • To: {selectedMessage.toName}
                      </div>

                      {selectedMessage.isAdminRecipient && (
                        <div className='mt-2'>
                          <span className='badge bg-primary me-2'>
                            Feature: {selectedMessage.feature || '—'}
                          </span>
                          <span className='badge bg-secondary'>
                            Issue: {selectedMessage.issue || '—'}
                          </span>
                        </div>
                      )}

                      <hr />
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedMessage.body}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* /right */}
            </div>
          </div>
        </div>
      </div>

      {/* COMPOSE MODAL */}
      {isComposeOpen && (
        <div
          className='modal fade show'
          style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}
        >
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <form onSubmit={handleSend}>
                <div className='modal-header'>
                  <h5 className='modal-title'>Compose Message</h5>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setIsComposeOpen(false)}
                  />
                </div>
                <div className='modal-body'>
                  <div className='row g-3'>
                    <div className='col-md-4'>
                      <label className='form-label'>Send to</label>
                      <select
                        className='form-select'
                        value={recipientType}
                        onChange={(e) => {
                          setRecipientType(e.target.value);
                          setRecipientId('');
                          setFeature('');
                          setIssue('');
                        }}
                      >
                        <option value='connection'>Connection</option>
                        <option value='admin'>Admin</option>
                      </select>
                    </div>

                    <div className='col-md-8'>
                      <label className='form-label'>Recipient</label>
                      <select
                        className='form-select'
                        value={recipientId}
                        onChange={(e) => setRecipientId(e.target.value)}
                      >
                        <option value=''>Select recipient</option>
                        {(recipientType === 'connection'
                          ? connectionOptions
                          : adminOptions
                        ).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {recipientType === 'admin' && (
                      <>
                        <div className='col-md-6'>
                          <label className='form-label'>Feature</label>
                          <select
                            className='form-select'
                            value={feature}
                            onChange={(e) => setFeature(e.target.value)}
                          >
                            <option value=''>Select a Feature</option>
                            {FEATURE_OPTIONS.map((f) => (
                              <option key={f} value={f}>
                                {f.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>Issue</label>
                          <select
                            className='form-select'
                            value={issue}
                            onChange={(e) => setIssue(e.target.value)}
                          >
                            <option value=''>Select an Issue</option>
                            {ISSUE_OPTIONS.map((i) => (
                              <option key={i} value={i}>
                                {i.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    <div className='col-12'>
                      <label className='form-label'>Subject</label>
                      <input
                        className='form-control'
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        maxLength={150}
                        placeholder='Subject'
                      />
                    </div>

                    <div className='col-12'>
                      <label className='form-label'>Message</label>
                      <textarea
                        className='form-control'
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder='Write your message...'
                      />
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => setIsComposeOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={!canSend}
                  >
                    Send
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
